// @ts-nocheck
import React, { useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTrainingFeed, TrainingFeedFilter, TrainingFeedSort, TrainingFeedCheckin, TrainingFeedRoutine } from '@/hooks/useTrainingFeed';
import { FeedCheckinCard } from '@/components/training/FeedCheckinCard';
import { FeedRoutineCard } from '@/components/training/FeedRoutineCard';
import { TrainingSidebar } from '@/components/training/TrainingSidebar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Flame, Dumbbell, Utensils, Bed, Film, Brain, Lock, HelpCircle } from 'lucide-react';
import { CommunityTrainingInstructions, TrainingHelpButton } from '@/components/training/TrainingInstructionBox';
import { LabsWelcomeAlert } from '@/components/labs/LabsWelcomeAlert';

const filterOptions: { value: TrainingFeedFilter; label: string; icon: React.ElementType }[] = [
  { value: 'all', label: 'All', icon: Flame },
  { value: 'workout', label: 'Workouts', icon: Dumbbell },
  { value: 'nutrition', label: 'Nutrition', icon: Utensils },
  { value: 'recovery', label: 'Recovery', icon: Bed },
  { value: 'film_study', label: 'Film Study', icon: Film },
  { value: 'mental', label: 'Mental', icon: Brain },
];

const TrainingFeedPage: React.FC = () => {
  const { user, profile, isLoading: authLoading } = useAuth();
  const [filter, setFilter] = React.useState<TrainingFeedFilter>('all');
  const [sort, setSort] = React.useState<TrainingFeedSort>('newest');
  
  const {
    feedItems,
    isLoading,
    hasMore,
    isLoadingMore,
    loadMore,
    topRoutines,
    followedAthletes,
    likeRoutine,
    isLiking,
    currentUserId,
  } = useTrainingFeed(filter, sort);

  // Infinite scroll setup
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  const setupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { rootMargin: '200px' }
    );

    if (loadMoreTriggerRef.current) {
      observerRef.current.observe(loadMoreTriggerRef.current);
    }
  }, [hasMore, isLoadingMore, loadMore]);

  useEffect(() => {
    setupObserver();
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [setupObserver]);

  // Show loading while auth is loading
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/auth/login" replace state={{ from: '/training' }} />;
  }

  // Check role access - only athletes and coaches
  const allowedRoles = ['athlete', 'coach', 'club_coach', 'college_coach', 'coach_college', 'admin'];
  const userRole = profile?.role;
  
  if (!userRole || !allowedRoles.includes(userRole)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md p-6">
          <Lock className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Access Restricted</h1>
          <p className="text-muted-foreground">
            You must be logged in as an athlete or coach to view this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6">
        {/* Labs Welcome Alert */}
        <LabsWelcomeAlert className="mb-6" />

        {/* Instructions Banner */}
        <CommunityTrainingInstructions />

        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Flame className="h-8 w-8 text-orange-500" />
              Training Feed
            </h1>
            <p className="text-muted-foreground mt-1">
              See how athletes are training and share your routines
            </p>
          </div>
          <TrainingHelpButton moduleId="training-community" />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Category Filter Tabs */}
          <Tabs value={filter} onValueChange={(v) => setFilter(v as TrainingFeedFilter)} className="flex-1">
            <TabsList className="w-full sm:w-auto overflow-x-auto">
              {filterOptions.map((opt) => (
                <TabsTrigger key={opt.value} value={opt.value} className="gap-1">
                  <opt.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{opt.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Sort Select */}
          <Select value={sort} onValueChange={(v) => setSort(v as TrainingFeedSort)}>
            <SelectTrigger className="w-full sm:w-40">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="most_liked">Most Liked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Feed Column */}
          <div className="lg:col-span-2 space-y-4">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : feedItems.length === 0 ? (
              <div className="text-center py-12 bg-muted/30 rounded-lg">
                <Flame className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium text-foreground mb-2">No training posts yet</h3>
                <p className="text-muted-foreground">
                  Be the first to share your training!
                </p>
              </div>
            ) : (
              <>
                {feedItems.map((item) => (
                  item.type === 'checkin' ? (
                    <FeedCheckinCard 
                      key={`checkin-${item.id}`} 
                      checkin={item as TrainingFeedCheckin} 
                    />
                  ) : (
                    <FeedRoutineCard
                      key={`routine-${item.id}`}
                      routine={item as TrainingFeedRoutine}
                      onLike={likeRoutine}
                      isLiking={isLiking}
                      currentUserId={currentUserId}
                    />
                  )
                ))}

                {/* Load More Trigger */}
                <div ref={loadMoreTriggerRef} className="h-4" />

                {isLoadingMore && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                )}

                {!hasMore && feedItems.length > 0 && (
                  <p className="text-center text-sm text-muted-foreground py-4">
                    You've reached the end
                  </p>
                )}
              </>
            )}
          </div>

          {/* Sidebar Column */}
          <div className="hidden lg:block">
            <div className="sticky top-6">
              <TrainingSidebar 
                topRoutines={topRoutines}
                followedAthletes={followedAthletes}
              />
            </div>
          </div>
        </div>

        {/* Mobile Sidebar - at bottom */}
        <div className="lg:hidden mt-8">
          <TrainingSidebar 
            topRoutines={topRoutines}
            followedAthletes={followedAthletes}
          />
        </div>
      </div>
    </div>
  );
};

export default TrainingFeedPage;
